import { CaretUpDownIcon, XIcon } from '@phosphor-icons/react';
import { useField } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Chip, Skeleton, Text } from '@components';
import { ANIMATION_DURATIONS } from '@constants';
import { InputMod, Translation, VariantType } from '@enums';
import type { MultipleSelectInputProps, SelectOptionType } from '@types';
import { cn, validateValueEqual } from '@utils';

import ErrorMessage from '../TextInput/errorMessage';

export const BaseMultipleSelectInput: React.FC<MultipleSelectInputProps> = ({
  name,
  options = [],
  placeholder = '',
  label = '',
  disabled = false,
  loading = false,
  format,
  className = '',
  withoutChip = false,
  errorClassName = '',
  customWidth,
  inputMod = InputMod.Default,
  maxSelections,
}) => {
  const [field, meta, helpers] = useField(name);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const inputRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isError = meta.error && meta.touched;
  const isFilled = validateValueEqual(InputMod.Filled, inputMod);

  const { t: tTable } = useTranslation(Translation.Common, { keyPrefix: 'tables' });
  const { t: tCommon } = useTranslation(Translation.Common, { keyPrefix: 'multiSelect' });

  const normalizedOptions = useMemo(() => {
    return options.map((option): SelectOptionType => {
      if (typeof option === 'object' && 'value' in option && 'label' in option) {
        return option as SelectOptionType;
      }
      return { value: option, label: String(option) };
    });
  }, [options]);

  const selectedValues = useMemo(() => {
    return Array.isArray(field.value)
      ? field.value.map(
          (value) =>
            normalizedOptions.find((option) => validateValueEqual(option.value, value)) || {
              value,
              label: String(value),
            },
        )
      : [];
  }, [field.value, normalizedOptions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = useCallback(() => {
    if (!disabled) {
      setIsOpen((prevIsOpen) => !prevIsOpen);
      if (!isOpen) {
        setTimeout(() => {
          const inputRect = inputRef.current?.getBoundingClientRect();
          const dropdownRect = dropdownRef.current?.getBoundingClientRect();
          if (inputRect && dropdownRect) {
            const viewportHeight = window.innerHeight;
            const spaceBelow = viewportHeight - inputRect.bottom;
            const spaceAbove = inputRect.top;
            const dropdownHeight = dropdownRect.height;

            setDropdownPosition(spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? 'top' : 'bottom');
          }
        }, ANIMATION_DURATIONS.FAST);
      }
    }
  }, [disabled, isOpen]);

  const handleOptionClick = useCallback(
    (option: SelectOptionType) => {
      const isSelected = selectedValues.some((item) => validateValueEqual(item.value, option.value));

      if (!isSelected && selectedValues.length >= (maxSelections ?? Infinity)) {
        return;
      }

      const newSelected = isSelected
        ? selectedValues.filter((item) => !validateValueEqual(item.value, option.value))
        : [...selectedValues, option];
      helpers.setValue(newSelected.map((item) => item.value));
    },
    [selectedValues, helpers, maxSelections],
  );

  const handleDeleteClick = useCallback(
    (optionToDelete: SelectOptionType) => {
      helpers.setValue(
        selectedValues
          .filter((item) => !validateValueEqual(item.value, optionToDelete.value))
          .map((item) => item.value),
      );
    },
    [selectedValues, helpers],
  );

  const handleSelectAll = useCallback(() => {
    helpers.setValue(
      selectedValues.length === normalizedOptions.length ? [] : normalizedOptions.map((option) => option.value),
    );
  }, [selectedValues, normalizedOptions, helpers]);

  const renderOptionLabel = useCallback(
    (option: SelectOptionType) => (
      <Text $customizeColor $level={6} className="flex-grow leading-4 ellipsis-text">
        {format ? format(option.label) : option.label}
      </Text>
    ),
    [format],
  );

  const isSelectedValuesMoreThanZero = selectedValues.length > 0;
  const inputClasses = cn(
    'block transition-all duration-200 hover:cursor-pointer',
    'text-us px-3.5 py-1.5',
    'flex w-full rounded-md shadow-sm items-center',
    'placeholder-gray-400 bg-white text-black',
    'border border-gray-300',
    'focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50',
    isError && 'ring-red-500',
    disabled && 'bg-gray-100 hover:cursor-not-allowed text-gray-400',
    isFilled &&
      (isSelectedValuesMoreThanZero
        ? 'pt-8 pb-3'
        : 'group-focus-within:pt-10 peer-valid:pt-10 peer-valid:pb-5 group-focus-within:pb-5'),
    className,
  );

  if (loading) return <Skeleton className="h-10 rounded-md w-full" />;

  return (
    <div className={cn('flex flex-col h-fit space-y-3 w-full', customWidth)}>
      <div className="relative group" ref={inputRef}>
        <div
          className={cn('min-h-10 flex flex-wrap gap-2 pr-8 py-2', inputClasses, isOpen && 'ring-2 ring-indigo-600')}
          onClick={handleToggle}
        >
          {withoutChip ? (
            <Text $level={6} $customizeColor className={isSelectedValuesMoreThanZero ? 'text-black' : 'text-gray-400'}>
              {isSelectedValuesMoreThanZero
                ? `${
                    validateValueEqual(selectedValues.length, 1)
                      ? tCommon('oneItemSelected')
                      : tCommon('itemsSelected', { count: selectedValues.length })
                  }`
                : placeholder}
            </Text>
          ) : isSelectedValuesMoreThanZero ? (
            selectedValues.map((item: SelectOptionType, index) => (
              <Chip
                key={index}
                smallFont
                chipVariant={VariantType.Outlined}
                className="px-2 py-1 w-fit"
                onChipClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(item);
                }}
                adornment={
                  <>
                    {renderOptionLabel(item)}
                    <XIcon className="size-4 self-center text-blue-400" />
                  </>
                }
              />
            ))
          ) : (
            <Text $level={6} $customizeColor className="text-gray-400 leading-4">
              {placeholder}
            </Text>
          )}
          <CaretUpDownIcon
            weight="bold"
            className="size-4 absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400"
          />
        </div>
        {isFilled && label && field.value?.length > 0 && (
          <Text
            $customizeColor
            $level={null}
            className={cn(
              'pointer-events-none absolute left-3.5 transition-all duration-200 font-medium text-smLabel top-2.5',
              (!withoutChip && isSelectedValuesMoreThanZero) || isOpen ? 'text-gray-500' : 'text-gray-400',
            )}
          >
            {label}
          </Text>
        )}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={dropdownRef}
              initial={{ opacity: 0, y: dropdownPosition === 'bottom' ? -10 : 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: dropdownPosition === 'bottom' ? -10 : 10 }}
              transition={{ duration: 0.2 }}
              className={cn(
                'absolute w-full z-20 my-2 max-h-60 sm:max-h-72 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
                dropdownPosition === 'bottom' && 'top-full',
                dropdownPosition === 'top' && 'bottom-full',
              )}
            >
              {options?.length > 0 && options?.length <= 5 && (
                <div
                  className="px-4 py-2 flex items-center cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                  onClick={handleSelectAll}
                >
                  <Text $level={6} className="flex-grow font-medium text-gray-700">
                    {tTable(selectedValues.length === normalizedOptions.length ? 'unselectAll' : 'selectAll')}
                  </Text>
                </div>
              )}
              <div className="overflow-y-auto max-h-32 sm:max-h-40">
                {normalizedOptions.map((option, index) => {
                  const isSelected = selectedValues.some((item) => validateValueEqual(item.value, option.value));
                  const isDisabled = !isSelected && selectedValues.length >= (maxSelections ?? Infinity);

                  return (
                    <div
                      key={index}
                      className={cn(
                        'items-center px-4 py-2 cursor-pointer transition-colors duration-200 flex',
                        isSelected ? 'bg-indigo-50 text-indigo-900' : 'text-gray-900 hover:bg-gray-100',
                        isDisabled && 'cursor-not-allowed opacity-50',
                      )}
                      onClick={() => !isDisabled && handleOptionClick(option)}
                    >
                      <input
                        type="checkbox"
                        className="form-checkbox size-4 text-indigo-600 rounded border-gray-300 focus:ring-0 mr-3"
                        checked={isSelected}
                        readOnly
                      />
                      {renderOptionLabel(option)}
                    </div>
                  );
                })}
              </div>
              {options.length === 0 && (
                <div className="text-gray-300 items-center py-2 px-4">
                  <Text $level={6} $customizeColor>
                    {tTable('noAvailableOption')}
                  </Text>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {isError && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <ErrorMessage isError={isError} errorClassName={errorClassName} meta={meta} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
